import { FC, ReactElement, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Select from 'react-select';

import { ErrorMessage } from '../components/ErrorMessage';
import { Loading } from '../components/Loading';
import ScheduleGantt, {
  formatQuizRange,
  getCurrentAcademicYearStart,
  getQuizStatus,
  ScheduleGanttZoom,
} from '../components/ScheduleGantt';
import SwitchButton from '../components/Switch';
import { quizscheduleTimeZone } from '../constants';
import {
  useGetScheduleOverviewQuery,
  useGetSchoolsQuery,
} from '../services/apiSlice';
import { ScheduleOverviewQuiz, ScheduleOverviewSchool } from '../types';

dayjs.extend(utc);
dayjs.extend(timezone);

const zoomLevels: ScheduleGanttZoom[] = ['months', 'weeks'];

// School-year imports run in late August, so allow a grace window before Sept 1
// when deciding whether a class belongs to the current academic year.
const importGraceDays = 45;

const prefersListView = () =>
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(max-width: 767px)').matches
    : false;

const statusBadgeStyles: Record<string, string> = {
  past: 'bg-gray-200 text-gray-600',
  current: 'bg-tttDefault text-white',
  upcoming: 'bg-blue-100 text-blue-700',
};

const legendItems = [
  { label: 'Past', style: 'bg-gray-300' },
  { label: 'Current', style: 'bg-tttDefault' },
  { label: 'Upcoming', style: 'bg-blue-200' },
  { label: 'Review quiz', style: 'bg-white ring-2 ring-amber-400' },
];

const filterToCurrentYear = (
  schools: ScheduleOverviewSchool[],
  academicYearStart: dayjs.Dayjs,
): ScheduleOverviewSchool[] => {
  const yearStart = academicYearStart.toDate();
  const graceStart = academicYearStart
    .subtract(importGraceDays, 'day')
    .toDate();

  return schools
    .map((school) => ({
      ...school,
      tracks: school.tracks
        .map((track) => ({
          ...track,
          schedule: track.schedule.filter(
            (quiz) => new Date(quiz.dueDate) >= yearStart,
          ),
        }))
        .filter(
          (track) =>
            track.schedule.length > 0 ||
            track.classes.some(
              (clazz) =>
                !!clazz.createdAt && new Date(clazz.createdAt) >= graceStart,
            ),
        ),
    }))
    .filter((school) => school.tracks.length > 0);
};

function ScheduleListMode({
  schools,
}: {
  schools: ScheduleOverviewSchool[];
}): ReactElement {
  const today = dayjs().tz(quizscheduleTimeZone).startOf('day');

  return (
    <div className="space-y-6" data-testid="schedule-list">
      {schools.map((school) => (
        <section key={school.school}>
          <h2 className="mb-2 text-lg font-semibold text-gray-800">
            {school.school}
          </h2>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {school.tracks.map((track) => {
              const openQuizzes = track.schedule
                .filter((quiz) => getQuizStatus(quiz, today) !== 'past')
                .slice(0, 4);
              const pastCount =
                track.schedule.length -
                track.schedule.filter(
                  (quiz) => getQuizStatus(quiz, today) !== 'past',
                ).length;
              return (
                <div
                  key={track.trackId}
                  className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                  data-testid="schedule-track-card"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-medium text-gray-800">
                      {track.displayedTrack}
                    </h3>
                    <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                      #{track.serialNum}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {track.classes.map((clazz) => (
                      <span
                        key={clazz.classId}
                        className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                        title={clazz.teacherName || undefined}
                      >
                        {clazz.className}
                      </span>
                    ))}
                    {track.classes.length === 0 && (
                      <span className="text-xs italic text-gray-400">
                        No classes assigned
                      </span>
                    )}
                  </div>
                  <ul className="mt-3 space-y-1">
                    {openQuizzes.map((quiz: ScheduleOverviewQuiz) => {
                      const status = getQuizStatus(quiz, today);
                      return (
                        <li
                          key={quiz.quiz}
                          className="flex items-center justify-between gap-2 text-sm"
                        >
                          <span className="text-gray-700">
                            {quiz.quiz}
                            {quiz.isReview ? ' (review)' : ''}
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">
                              {formatQuizRange(quiz)}
                            </span>
                            <span
                              className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${statusBadgeStyles[status]}`}
                            >
                              {status}
                            </span>
                          </span>
                        </li>
                      );
                    })}
                    {track.schedule.length === 0 && (
                      <li className="text-xs italic text-gray-400">
                        No schedule
                      </li>
                    )}
                  </ul>
                  <p className="mt-2 text-xs text-gray-400">
                    {pastCount} past quizzes
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      ))}
      {schools.length === 0 && (
        <p className="text-sm text-gray-500">No schools found.</p>
      )}
    </div>
  );
}

const ScheduleOverview: FC = () => {
  const [schoolFilter, setSchoolFilter] = useState<string | undefined>(
    undefined,
  );
  const [view, setView] = useState<'timeline' | 'list'>(() =>
    prefersListView() ? 'list' : 'timeline',
  );
  const [zoom, setZoom] = useState<ScheduleGanttZoom>('weeks');
  const [showPastYears, setShowPastYears] = useState(false);

  const { data: overview, isLoading, isError } = useGetScheduleOverviewQuery();
  const { data: schoolNames } = useGetSchoolsQuery();

  const academicYearStart = useMemo(() => getCurrentAcademicYearStart(), []);

  const schools = useMemo(() => {
    const base =
      overview?.schools.filter(
        (school) => !schoolFilter || school.school === schoolFilter,
      ) ?? [];
    return showPastYears ? base : filterToCurrentYear(base, academicYearStart);
  }, [overview, schoolFilter, showPastYears, academicYearStart]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold text-gray-800">Schedules</h1>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="min-w-[220px]" data-testid="school-filter">
          <Select
            value={
              schoolFilter
                ? { value: schoolFilter, label: schoolFilter }
                : undefined
            }
            onChange={(selected) => setSchoolFilter(selected?.value)}
            placeholder="All schools"
            isClearable
            options={(schoolNames ?? []).map((school) => ({
              value: school,
              label: school,
            }))}
          />
        </div>
        <div
          className="flex overflow-hidden rounded-lg border border-gray-300"
          data-testid="view-toggle"
        >
          <button
            className={`px-3 py-1.5 text-sm ${
              view === 'timeline'
                ? 'bg-tttDefault text-white'
                : 'bg-white text-gray-700'
            }`}
            onClick={() => setView('timeline')}
            data-testid="view-timeline-button"
          >
            Timeline
          </button>
          <button
            className={`px-3 py-1.5 text-sm ${
              view === 'list'
                ? 'bg-tttDefault text-white'
                : 'bg-white text-gray-700'
            }`}
            onClick={() => setView('list')}
            data-testid="view-list-button"
          >
            List
          </button>
        </div>
        {view === 'timeline' && (
          <div
            className="flex overflow-hidden rounded-lg border border-gray-300"
            data-testid="zoom-control"
          >
            {zoomLevels.map((level) => (
              <button
                key={level}
                className={`px-3 py-1.5 text-sm capitalize ${
                  zoom === level
                    ? 'bg-tttDefault text-white'
                    : 'bg-white text-gray-700'
                }`}
                onClick={() => setZoom(level)}
                data-testid={`zoom-${level}-button`}
              >
                {level}
              </button>
            ))}
          </div>
        )}
        <div
          className="flex items-center gap-2"
          data-testid="show-past-years-toggle"
        >
          <SwitchButton
            checked={showPastYears}
            onChange={setShowPastYears}
            id="show-past-years"
            shouldHaveText={false}
            title="Show schedules from previous academic years"
          />
          <span className="text-sm text-gray-600">Show past years</span>
        </div>
      </div>

      {view === 'timeline' && (
        <div
          className="mb-3 flex flex-wrap gap-4"
          data-testid="schedule-legend"
        >
          {legendItems.map((item) => (
            <span
              key={item.label}
              className="flex items-center gap-1.5 text-xs text-gray-500"
            >
              <span className={`inline-block h-3 w-5 rounded ${item.style}`} />
              {item.label}
            </span>
          ))}
        </div>
      )}

      {isLoading && <Loading />}
      {isError && <ErrorMessage text="Couldn't load schedules." />}
      {!isLoading && !isError && overview && (
        <>
          {schools.length === 0 ? (
            <p
              className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500"
              data-testid="schedule-empty"
            >
              No schedules found for the current academic year.
              {!showPastYears && overview.schools.length > 0 && (
                <>
                  {' '}
                  Older schedules exist — enable “Show past years” to reveal
                  them.
                </>
              )}
            </p>
          ) : view === 'timeline' ? (
            <ScheduleGantt
              schools={schools}
              zoom={zoom}
              startAt={
                showPastYears ? undefined : academicYearStart.toISOString()
              }
            />
          ) : (
            <ScheduleListMode schools={schools} />
          )}
        </>
      )}
    </div>
  );
};

export default ScheduleOverview;
